<template>
  <div>
    <region-add-new
      :is-add-new-region-sidebar-active.sync="isAddNewRegionSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <region-edit
      :is-edit-region-sidebar-active.sync="isEditRegionSidebarActive"
      :region-id="regionId"
      :region-name="regionName"
      :region-code="regionCode"
      @refetch-data="refetchData"
    />

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewRegionSidebarActive = true"
              >
                <span class="text-nowrap">Add Region</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refRegionListTable"
        class="position-relative"
        :items="fetchRegions"
        responsive
        :fields="tableColumns"
        primary-key="region_id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 4"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(region_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region_name }}</span>
          </div>
        </template>

        <template #cell(region_code)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.region_code }}</span>
          </div>
        </template>

        <template #cell(branches)="data">
          <div
            v-if="data.item.branches && data.item.branches.length"
            class="d-flex flex-wrap gap-1"
          >
            <span
              v-for="branch in data.item.branches"
              :key="branch.branch_id"
              class="custom-tag"
            >
              {{ branch.branch_name }} ({{ branch.branch_code }})
            </span>
          </div>
          <div v-else>
            <span class="text-muted">No branches</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editRegion(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteRegion(data.item.region_id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRegions"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BPagination, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import { ref } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import apiEndpoints from '@/constants/apiEndpoints'
import useRegionsList from './RegionBranchesList' // Moved below the aliased @core import!

// Import your Sidebars here (adjust paths if necessary based on your file structure)
import RegionAddNew from './RegionAddNew.vue'
import RegionEdit from './RegionEdit.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    vSelect,
    RegionAddNew,
    RegionEdit,
  },
  setup() {
    const isAddNewRegionSidebarActive = ref(false)
    const isEditRegionSidebarActive = ref(false)

    const regionId = ref('')
    const regionName = ref('')
    const regionCode = ref('')

    const {
      fetchRegions,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalRegions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRegionListTable,
      refetchData,
    } = useRegionsList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      isAddNewRegionSidebarActive,
      isEditRegionSidebarActive,
      regionId,
      regionName,
      regionCode,
      roleOptions,
      fetchRegions,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalRegions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRegionListTable,
      refetchData,
    }
  },
  mounted() {
    this.$root.$on('refreshTable', () => {
      if (this.$refs.refRegionListTable) {
        if (this.$refs.refRegionListTable) this.$refs.refRegionListTable.refresh()
      }
    })
  },
  methods: {
    editRegion(item) {
      this.regionId = item.region_id
      this.regionName = item.region_name
      this.regionCode = item.region_code
      this.isEditRegionSidebarActive = true
    },
    deleteRegion(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_REGION}/${id}` // Replace with your actual delete endpoint

      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            if (this.$refs.refRegionListTable) this.$refs.refRegionListTable.refresh()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Region has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-tag {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #007bff;
  background-color: #f8f9fa;
  border-radius: 4px;
  color: #333;
  margin-right: 0.25rem;
  line-height: 1.2;
}

.custom-tag:hover {
  background-color: #e9ecef;
  cursor: default;
}

.gap-1 {
  gap: 0.25rem;
}

.text-muted {
  font-size: 0.75rem;
}

.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>